import { useTranslation } from "react-i18next"

import { formatPriceRange } from "./format-price"

/**
 * OKS-1018 — THE catalogue price range, shared by every surface that shows one:
 * the Produits table and card grid (`brand/settings/products/`), the mission
 * catalogue picker row and the attached-product summary card (`missions/`).
 * One component so the four can never disagree — OKS-1008 shipped four inline
 * copies kept "byte-identical" by comment, which is how all four came to share
 * the same a11y defect.
 *
 * Renders a FRAGMENT, never a wrapper: each host keeps its own `<TableCell>`,
 * `<p>` or `<span>` and its own testid.
 *
 * | State                         | Visible               | Announced              |
 * |-------------------------------|-----------------------|------------------------|
 * | range (min ≠ max)             | `49,00 € – 129,00 €`  | `49,00 € à 129,00 €`   |
 * | single (max null or = min)    | `49,00 €`             | `49,00 €`              |
 * | unpriced (min null/undefined) | nothing               | nothing                |
 *
 * # The connector (BRIEF-OKS-1018 copy ruling (a))
 *
 * The en dash is `aria-hidden` — announced it would be read "tiret" / "dash" —
 * so without a word a screen reader hears two prices, not a range. The word is
 * an `sr-only` node beside the dash: in the accessibility tree, not on screen.
 * The dash is an element and the connector a single-word key — amounts are
 * never inside a translated string (OKS-931 UX §5).
 *
 * ## ⚠️ The spaces are TEXT NODES OUTSIDE both spans — measured, keep it so
 *
 * Moving a space inside a span looks harmless in review and is a regression:
 *   · `<span aria-hidden> – </span>` — the hidden span takes its spaces with it,
 *     so the announced text runs the amounts together (`49,00 €129,00 €`).
 *   · `<span className="sr-only"> à </span>` — Chrome still reads it correctly
 *     (it pads the absolutely-positioned node), but jsdom's name engine trims
 *     text inside an element and reads `49,00 €à129,00 €`.
 * Only this shape reads `49,00 € à 129,00 €` in BOTH engines (jsdom, and
 * Chrome's AX tree via CDP `Accessibility.getPartialAXTree`), and with
 * `.sr-only` removed its text is byte-identical to the pre-OKS-1018 rendering.
 * Copy-paste picks up the hidden word (`49,00 € –à 129,00 €`) — accepted (OQ3).
 *
 * # Carried over from the OKS-1008 mission copies
 *
 * ⚠️ `formatPriceRange`, NOT `formatGiftValue`. The gift formatter forces
 * `minimumFractionDigits: 2` — right for a gift value, wrong for a catalogue
 * price in a zero-decimal currency (`4 900,00 JPY` where the honest rendering
 * is `4 900 JPY`).
 *
 * ⚠️ `i18n.language` is passed RAW because it is guaranteed to be exactly
 * `"fr"` or `"en"` (`lib/i18n/client.ts` sets `lng` from the server `Locale`
 * with `supportedLngs`, and `syncI18nLocale` rejects anything else).
 * `formatPrice` branches on a bare `language === "en"`, so a regional tag like
 * `"en-US"` would silently render fr-FR. Do not re-narrow it locally.
 *
 * ⚠️ `currency` is per ROW — a manual product keeps the currency it was entered
 * in even on a brand whose store declares another one.
 */
export function PriceRange({
  minCents,
  maxCents,
  currency,
}: {
  minCents: number | null | undefined
  maxCents: number | null | undefined
  currency: string
}) {
  const { t, i18n } = useTranslation()

  // ⚠️ `== null` is deliberately wider than `formatPrice`'s `=== null`: an
  // unpriced product renders NO price, never "NaN €" or "0,00 €".
  if (minCents == null) return null

  const range = formatPriceRange(
    minCents,
    maxCents ?? null,
    currency,
    i18n.language,
  )
  if (range === null) return null

  return (
    <>
      {range.min}
      {range.max !== null ? (
        <>
          {" "}
          <span aria-hidden="true">–</span>
          <span className="sr-only">{t("price_range.connector")}</span>{" "}
          {range.max}
        </>
      ) : null}
    </>
  )
}
